/* START_MODULE_CONTRACT
 * PURPOSE: Глобальная MUI-тема: палитра, поведение полей ввода и единая плавность анимаций.
 * SCOPE: createTheme, transitions, overrides (InputBase, InputLabel, OutlinedInput, Dialog, Button, CssBaseline).
 * DEPENDS: @mui/material/styles
 * LINKS: M-FE-APP
 * ROLE: CONFIG
 * MAP_MODE: EXPORTS
 * END_MODULE_CONTRACT
 *
 * START_MODULE_MAP
 * appTheme - тема приложения с переопределениями компонентов MUI.
 * END_MODULE_MAP
 *
 * START_CHANGE_SUMMARY
 * LAST_CHANGE: 2026-07-13 - Диалоги: Slide вместо Fade (Win11/Chromium caret bug); усиленный caretColor на input.
 * LAST_CHANGE: 2026-07-13 - Глобально более плавные анимации: мягкие easing, длительности, Fade-диалоги, Collapse/Menu/Backdrop.
 * LAST_CHANGE: 2026-07-13 - Стили MuiPickersDay/MuiDateCalendar для современного календаря.
 * LAST_CHANGE: 2026-07-13 - Плавность UI: кривые/длительности transitions, Grow для диалогов, transition кнопок, prefers-reduced-motion.
 * LAST_CHANGE: 2026-07-13 - Светлый sidebar в тон header (единая shell-палитра).
 * END_CHANGE_SUMMARY
 */

import React from 'react';
import { createTheme, alpha } from '@mui/material/styles';
import Slide from '@mui/material/Slide';

/** Slide вместо Fade: opacity на контейнере ломает каретку в Chromium на Windows 11. */
const DialogSlideTransition = React.forwardRef(function DialogSlideTransition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const brandColors = {
  deep: '#0e1a24',
  deepMid: '#152433',
  surface: '#f4f6f8',
  ink: '#152028',
  muted: '#5c6b78',
  action: '#2563a8',
  actionHover: '#1d4f8c',
};

/** Контрастная каретка и текст на светлых полях (Win11/Chromium compositor). */
const formFieldInputStyles = {
  color: brandColors.ink,
  caretColor: brandColors.ink,
  colorScheme: 'light',
};

export const shellColors = {
  sidebarBg: '#ffffff',
  sidebarText: brandColors.ink,
  sidebarTextMuted: brandColors.muted,
  sidebarHover: alpha(brandColors.ink, 0.04),
  sidebarSelected: alpha(brandColors.action, 0.1),
  sidebarBorder: alpha(brandColors.ink, 0.1),
  sidebarAccent: brandColors.action,
  headerBg: brandColors.surface,
  headerBorder: alpha(brandColors.ink, 0.08),
  canvasBg: '#e8edf2',
};

export function sidebarNavItemSx(selected) {
  return {
    borderRadius: 0.5,
    mx: 1,
    mb: 0.25,
    minHeight: 44,
    color: shellColors.sidebarText,
    '&:hover': { bgcolor: shellColors.sidebarHover },
    ...(selected && {
      bgcolor: shellColors.sidebarSelected,
      fontWeight: 600,
      '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: '20%',
        bottom: '20%',
        width: 3,
        borderRadius: '0 2px 2px 0',
        bgcolor: shellColors.sidebarAccent,
        transition: 'opacity 0.36s cubic-bezier(0.22, 1, 0.36, 1)',
      },
    }),
    '& .MuiListItemIcon-root': {
      color: selected ? shellColors.sidebarAccent : shellColors.sidebarTextMuted,
    },
  };
}

export const drawerPaperSx = {
  bgcolor: shellColors.sidebarBg,
  color: shellColors.sidebarText,
  borderRight: 1,
  borderColor: shellColors.sidebarBorder,
};

/** Единые тайминги для shell-анимаций (sidebar, layout). */
export function createDrawerWidthTransition(theme) {
  return theme.transitions.create(['width', 'margin', 'padding'], {
    easing: theme.transitions.easing.easeOut,
    duration: theme.transitions.duration.enteringScreen,
  });
}

/** Стандартный набор свойств для интерактивных поверхностей. */
export function createInteractiveSurfaceTransition(theme, properties = ['background-color', 'color', 'border-color', 'box-shadow']) {
  return theme.transitions.create(properties, {
    easing: theme.transitions.easing.easeOut,
    duration: theme.transitions.duration.shorter,
  });
}

const collapseTimeout = { enter: 360, exit: 280 };
const overlayTimeout = { appear: 360, enter: 360, exit: 280 };

/* START_METHOD_createAppTheme */
/* START_CONTRACT: createAppTheme
 * PURPOSE: Создать тему с UX-правилами для текстовых полей ввода.
 * INPUTS: {}.
 * OUTPUTS: { Theme }.
 * SIDE_EFFECTS: нет.
 * LINKS: M-FE-APP
 * END_CONTRACT: createAppTheme
 */
export const appTheme = createTheme({
  palette: {
    primary: {
      main: brandColors.action,
      dark: brandColors.actionHover,
      light: '#4d8fd4',
      contrastText: '#ffffff',
    },
    background: {
      default: shellColors.canvasBg,
      paper: '#ffffff',
    },
    text: {
      primary: brandColors.ink,
      secondary: brandColors.muted,
    },
  },
  shape: {
    borderRadius: 4,
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  transitions: {
    duration: {
      shortest: 180,
      shorter: 240,
      short: 300,
      standard: 360,
      complex: 440,
      enteringScreen: 360,
      leavingScreen: 280,
    },
    easing: {
      // Мягкое замедление в конце — ощущается плавнее стандартного Material.
      easeInOut: 'cubic-bezier(0.45, 0, 0.55, 1)',
      easeOut: 'cubic-bezier(0.22, 1, 0.36, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      sharp: 'cubic-bezier(0.4, 0, 0.2, 1)',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: (theme) => ({
        '@media (prefers-reduced-motion: no-preference)': {
          html: {
            scrollBehavior: 'smooth',
          },
        },
        // Доступность: уважаем системную настройку «уменьшить движение».
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            animationDuration: '0.01ms !important',
            animationIterationCount: '1 !important',
            transitionDuration: '0.01ms !important',
            scrollBehavior: 'auto !important',
          },
        },
        // Native-поля (auth и прочие вне MUI): стандартный I-beam при наведении.
        'input:not([type="button"]):not([type="submit"]):not([type="reset"]):not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([type="color"]):not([type="file"]):not(:disabled):not([readonly])':
          {
            cursor: 'text',
            ...formFieldInputStyles,
          },
        'textarea:not(:disabled):not([readonly])': {
          cursor: 'text',
          ...formFieldInputStyles,
        },
        'input:disabled, textarea:disabled': {
          cursor: 'not-allowed',
        },
      }),
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          pointerEvents: 'none !important',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          cursor: 'text',
          colorScheme: 'light',
          '&.Mui-disabled': {
            cursor: 'not-allowed',
          },
        },
        input: {
          cursor: 'text',
          ...formFieldInputStyles,
        },
        textarea: {
          cursor: 'text',
          ...formFieldInputStyles,
        },
      },
    },
    MuiBackdrop: {
      defaultProps: {
        transitionDuration: overlayTimeout,
      },
    },
    MuiCollapse: {
      defaultProps: {
        timeout: collapseTimeout,
      },
    },
    MuiFade: {
      defaultProps: {
        timeout: overlayTimeout,
      },
    },
    MuiGrow: {
      defaultProps: {
        timeout: overlayTimeout,
      },
    },
    MuiZoom: {
      defaultProps: {
        timeout: overlayTimeout,
      },
    },
    MuiDialog: {
      defaultProps: {
        slots: { transition: DialogSlideTransition },
        transitionDuration: overlayTimeout,
      },
      styleOverrides: {
        paper: {
          colorScheme: 'light',
        },
      },
    },
    MuiMenu: {
      defaultProps: {
        transitionDuration: overlayTimeout,
      },
    },
    MuiPopover: {
      defaultProps: {
        transitionDuration: overlayTimeout,
      },
    },
    MuiTooltip: {
      defaultProps: {
        enterDelay: 400,
        enterNextDelay: 280,
        leaveDelay: 80,
        TransitionProps: { timeout: { enter: 240, exit: 180 } },
      },
    },
    MuiSnackbar: {
      defaultProps: {
        TransitionProps: { timeout: collapseTimeout },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          transition: createInteractiveSurfaceTransition(theme, [
            'background-color',
            'box-shadow',
            'border-color',
            'color',
            'transform',
          ]),
        }),
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          transition: createInteractiveSurfaceTransition(theme),
        }),
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          position: 'relative',
          transition: createInteractiveSurfaceTransition(theme, ['background-color', 'color']),
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          transition: theme.transitions.create(['box-shadow', 'border-color'], {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.shorter,
          }),
        }),
      },
    },
    MuiSwitch: {
      styleOverrides: {
        thumb: ({ theme }) => ({
          transition: theme.transitions.create(['transform', 'background-color'], {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.shorter,
          }),
        }),
        track: ({ theme }) => ({
          transition: theme.transitions.create(['background-color', 'opacity'], {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.shorter,
          }),
        }),
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          cursor: 'text',
          colorScheme: 'light',
          transition: theme.transitions.create(['border-color', 'box-shadow'], {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.shorter,
          }),
        }),
        input: formFieldInputStyles,
      },
    },
    MuiPickersDay: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.shape.borderRadius,
          fontSize: '0.875rem',
          transition: createInteractiveSurfaceTransition(theme, ['background-color', 'color', 'box-shadow']),
          '&.Mui-selected': {
            fontWeight: 600,
          },
          '&.MuiPickersDay-today': {
            borderColor: theme.palette.primary.main,
          },
        }),
      },
    },
    MuiDateCalendar: {
      styleOverrides: {
        root: {
          '& .MuiPickersCalendarHeader-label': {
            fontWeight: 600,
          },
        },
      },
    },
  },
});
/* END_METHOD_createAppTheme */
